// src/components/UI/CreditRequestModal.js
import React, { useState, useContext, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './CreditRequestModal.module.css';
import { FaInfoCircle } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';
import axios from 'axios';
import starIcon from '../../assets/dashboard_assets/star.png';

const CreditRequestModal = ({ isOpen, onClose }) => {
  const [creditAmount, setCreditAmount] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [isCheckingPending, setIsCheckingPending] = useState(true);
  const authCtx = useContext(AuthContext);
  
  // Check for pending requests when the modal opens
  useEffect(() => {
    if (isOpen) {
      checkPendingRequests();
    }
  }, [isOpen]);

  // Function to check for pending requests
  const checkPendingRequests = async () => {
    setIsCheckingPending(true);
    setError(null);
    
    try {
      const token = authCtx.token;
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      const response = await axios.get(
        `${API_URL}/auth/my_credit_requests`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Check for pending requests
      const requests = response.data.credit_requests || [];
      const pending = requests.find(req => req.status === 'pending');
      
      if (pending) {
        setPendingRequest(pending);
      } else {
        setPendingRequest(null);
      }
    } catch (err) {
      setError("Failed to check for pending requests. Please try again later.");
    } finally {
      setIsCheckingPending(false);
    }
  };

  const handleCreditChange = (e) => {
    // Parse input value as integer
    const value = parseInt(e.target.value, 10);
    
    // Validate the value is a number and within range
    if (!isNaN(value)) {
      // Ensure the value is between 1 and 50
      const validValue = Math.min(Math.max(value, 1), 50);
      setCreditAmount(validValue);
    } else {
      setCreditAmount(""); // Allow empty field for UX
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Double-check value is valid before submitting
    if (isNaN(creditAmount) || creditAmount < 1 || creditAmount > 50) {
      setError("Please enter a valid amount between 1 and 50");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get the auth token from context
      const token = authCtx.token;
      
      // Get API URL from environment or use default
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Make API request to request credits
      const response = await axios.post(
        `${API_URL}/auth/request_credit`, 
        { credit: creditAmount },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Handle successful response
      if (response.status === 200) {
        // Show success message
        setSuccess(response.data.message || `Successfully requested ${creditAmount} credits. Your request is pending admin approval.`);
        
        // Reset credit amount
        setCreditAmount(1);
        
        // Refresh pending request status
        await checkPendingRequests();
        
        // Auto-close after 3 seconds on success
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      // Handle error cases
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get user's current credits safely
  const currentCredits = authCtx.user && typeof authCtx.user.credits === 'number' 
    ? authCtx.user.credits 
    : 0;
  
  // Calculate how many more credits the user can request
  const maxRequestable = Math.max(0, 55 - currentCredits);

  // Format date for display
  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toLocaleDateString();
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  // Render pending request view if there's a pending request
  if (pendingRequest) {
    return ReactDOM.createPortal(
      <div className={styles.modalOverlay} onClick={onClose}>
        <div className={styles.modal} role="dialog" aria-labelledby="credit-request-title" onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.titleSection}>
              <h2 id="credit-request-title" className={styles.modalTitle}>
                <img src={starIcon} alt="star" className={styles.starIcon} /> Pending Credit Request
              </h2>
              <div className={styles.titleSeparator}></div>
            </div>
            <button 
              className={styles.closeButton} 
              onClick={onClose}
              aria-label="Close"
            >
              <div className={styles.closeIcon}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050"/>
                </svg>
              </div>
            </button>
          </div>
          
          <div className={styles.modalBody}>
            <div className={styles.pendingRequest}>
              <div className={styles.pendingHeader}>
                <p> Your credit request has been placed successfully.</p>
              </div>
              
              <div className={styles.pendingDetails}>
                <p><strong>Requested credits:</strong> {pendingRequest.requested_credit} credits</p>
                <p><strong>Requested on:</strong> {formatDate(pendingRequest.requested_at)}</p>
                <p><strong>Status: </strong> <span className={styles.pendingStatus}>Pending</span></p>
              </div>
              
              <p className={styles.pendingNote}>
              Your request is being reviewed by our team. The credits will be added to your account post approval process.
              </p>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                onClick={onClose}
                className={styles.submitButton}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Main credit request form
  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} role="dialog" aria-labelledby="credit-request-title" onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.titleSection}>
            <h2 id="credit-request-title" className={styles.modalTitle}>
              <img src={starIcon} alt="star" className={styles.starIcon} /> Request Credits
            </h2>
            <div className={styles.titleSeparator}></div>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={onClose}
            aria-label="Close"
          >
            <div className={styles.closeIcon}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 1.4L14.6 0L8 6.6L1.4 0L0 1.4L6.6 8L0 14.6L1.4 16L8 9.4L14.6 16L16 14.6L9.4 8L16 1.4Z" fill="#505050"/>
              </svg>
            </div>
          </button>
        </div>
        
        <div className={styles.modalBody}>
          <div className={styles.creditInfo}>
            <p>Current balance: <span className={styles.creditBalance}>{isCheckingPending ? '...' : currentCredits}</span> credits</p>
            <p>Maximum allowed: <span className={styles.creditLimit}>55</span> credits</p>
            <p>You can request up to <span className={styles.creditAvailable}>{isCheckingPending ? '...' : maxRequestable}</span> more credits</p>
          </div>
          
          <div className={styles.noteInfo}>
          <FaInfoCircle className={styles.infoIcon} />
            <p> 
            Credits requests are reviewed by our team. The credits will reflect in your account post approval.
            </p>
          </div>
          
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
          
          {success && (
            <div className={styles.successMessage}>
              {success}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className={styles.creditForm}>
            <div className={styles.inputGroup}>
              <label htmlFor="credit-amount" className={styles.inputLabel}>
                How many credits would you like to request?
              </label>
              <div className={styles.creditInputContainer}>
                <input
                  id="credit-amount"
                  type="number"
                  min="1"
                  placeholder='Enter number of credits'
                  max={isCheckingPending ? 50 : (maxRequestable > 0 ? Math.min(50, maxRequestable) : 1)}
                  className={styles.creditInput}
                  value={creditAmount}
                  onChange={handleCreditChange}
                  disabled={isLoading || isCheckingPending || maxRequestable <= 0}
                  required
                />
              </div>
            </div>
            
            <div className={styles.modalActions}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isLoading || isCheckingPending || maxRequestable <= 0}
              >
                {isLoading ? 'Processing...' : 'Request Credits'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreditRequestModal;